import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from '@/registry/reapit/lib/slot'

import { cn } from '@/registry/reapit/lib/utils'

const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:ring-ring/50 [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      colour: {
        neutral: 'bg-badge-neutral text-badge-neutral-foreground',
        success: 'bg-badge-success text-badge-success-foreground',
        pending: 'bg-badge-pending text-badge-pending-foreground',
        warning: 'bg-badge-warning text-badge-warning-foreground',
        danger: 'bg-badge-danger text-badge-danger-foreground',
        inactive: 'bg-badge-inactive text-badge-inactive-foreground',
        'accent-1': 'bg-badge-accent-1 text-badge-accent-1-foreground',
        'accent-2': 'bg-badge-accent-2 text-badge-accent-2-foreground',
      },
      variant: {
        default: '', // light bg + coloured text (from colour prop)
        reversed: '', // solid bg + white text — handled via compound variants
      },
    },
    compoundVariants: [
      { colour: 'neutral', variant: 'reversed', className: 'bg-badge-neutral-foreground text-white' },
      { colour: 'success', variant: 'reversed', className: 'bg-badge-success-foreground text-white' },
      { colour: 'pending', variant: 'reversed', className: 'bg-badge-pending-foreground text-white' },
      { colour: 'warning', variant: 'reversed', className: 'bg-badge-warning-foreground text-white' },
      { colour: 'danger', variant: 'reversed', className: 'bg-badge-danger-foreground text-white' },
      { colour: 'inactive', variant: 'reversed', className: 'bg-badge-inactive-foreground text-white' },
      { colour: 'accent-1', variant: 'reversed', className: 'bg-badge-accent-1-foreground text-white' },
      { colour: 'accent-2', variant: 'reversed', className: 'bg-badge-accent-2-foreground text-white' },
    ],
    defaultVariants: {
      colour: 'neutral',
      variant: 'default',
    },
  },
)

function Badge({
  className,
  colour,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span'

  return (
    <Comp
      data-slot="badge"
      data-colour={colour}
      data-variant={variant}
      className={cn(badgeVariants({ colour, variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
